import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { createPortal } from "react-dom";

import { telegramWebApp } from "../../telegram/webapp";
import type { PersonalCustomEmoji } from "../../api/client";
import { CustomEmojiPicker } from "../custom-emoji/CustomEmojiPicker";
import { useCustomEmojiLibrary } from "../custom-emoji/useCustomEmojiLibrary";
import { guardedHttpUrl } from "./appearance-model";
import {
  addButton, addRow, moveButton, moveRow, removeButton, removeRow,
  DEFAULT_BUTTON_LAYOUT_LIMITS,
  type ButtonLayoutLimits, type ButtonPosition, type EditableButtonRow, type EditableLayoutButton
} from "./button-layout-model";

type Props = { rows: EditableButtonRow[]; onChange: (rows: EditableButtonRow[]) => void; richEnabled: boolean; limits?: ButtonLayoutLimits };
type DragState = { source: ButtonPosition; target: ButtonPosition; name: string; pointerId: number | null; x: number; y: number };

const totalButtons = (rows: readonly EditableButtonRow[]) => rows.reduce((total, row) => total + row.buttons.length, 0);
const newId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;
const cloneRows = (rows: readonly EditableButtonRow[]) => rows.map((row) => ({ ...row, buttons: row.buttons.map((button) => ({ ...button })) }));
const styleLabels: Record<EditableLayoutButton["style"], string> = {
  default: "Обычная", primary: "Синяя", success: "Зелёная", danger: "Красная"
};

function ButtonEmojiPicker({
  onSelect,
  onClose
}: {
  onSelect: (emoji: PersonalCustomEmoji) => void;
  onClose: () => void;
}) {
  const library = useCustomEmojiLibrary();
  return <CustomEmojiPicker
    sets={library.sets}
    recent={library.recent}
    loadPreview={library.loadPreview}
    onClose={onClose}
    onSelect={(emoji) => {
      void library.markUsed(emoji);
      onSelect(emoji);
    }}
    onImport={() => telegramWebApp()?.openTelegramLink?.("https://t.me/THLS_AllPrice_bot?start=emoji_import")}
  />;
}

export function ButtonLayoutEditor({ rows, onChange, richEnabled, limits = DEFAULT_BUTTON_LAYOUT_LIMITS }: Props) {
  const [announcement, setAnnouncement] = useState("");
  const [selected, setSelected] = useState<ButtonPosition | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [undoRows, setUndoRows] = useState<EditableButtonRow[] | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const dragRef = useRef<DragState | null>(null);

  useEffect(() => () => telegramWebApp()?.enableVerticalSwipes?.(), []);

  const commit = (next: EditableButtonRow[], message: string, remember = true) => {
    if (remember) setUndoRows(cloneRows(rows));
    onChange(next);
    setAnnouncement(message);
  };
  const updateButton = (position: ButtonPosition, patch: Partial<EditableLayoutButton>) => onChange(rows.map((row, rowIndex) => ({
    ...row,
    buttons: row.buttons.map((button, buttonIndex) => rowIndex === position.row && buttonIndex === position.button ? { ...button, ...patch } : button)
  })));
  const shiftButton = (from: ButtonPosition, to: ButtonPosition, message: string) => {
    commit(moveButton(rows, from, to), message);
    setSelected(null);
  };
  const updateDrag = (next: DragState) => { dragRef.current = next; setDrag(next); };
  const finishDrag = () => {
    const current = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    telegramWebApp()?.enableVerticalSwipes?.();
    if (current) shiftButton(current.source, current.target, `${current.name} перемещена`);
  };
  const cancelDrag = () => { dragRef.current = null; setDrag(null); telegramWebApp()?.enableVerticalSwipes?.(); setAnnouncement("Перемещение отменено"); };
  const positionFromPoint = (x: number, y: number): ButtonPosition | null => {
    const element = document.elementFromPoint?.(x, y) as HTMLElement | null;
    const item = element?.closest<HTMLElement>("[data-button-position]");
    if (item) {
      const [row, button] = (item.dataset.buttonPosition ?? "").split(":").map(Number);
      if (Number.isInteger(row) && Number.isInteger(button)) return { row, button };
    }
    const row = element?.closest<HTMLElement>("[data-row-index]");
    if (row) {
      const rowIndex = Number(row.dataset.rowIndex);
      if (Number.isInteger(rowIndex)) return { row: rowIndex, button: rows[rowIndex]?.buttons.length ?? 0 };
    }
    return null;
  };
  const onDragMove = (event: PointerEvent<HTMLButtonElement>) => {
    const current = dragRef.current;
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    const target = positionFromPoint(event.clientX, event.clientY) ?? current.target;
    updateDrag({ ...current, target, x: event.clientX, y: event.clientY });
    if (event.clientY < 72) window.scrollBy({ top: -20, behavior: "auto" });
    else if (event.clientY > window.innerHeight - 72) window.scrollBy({ top: 20, behavior: "auto" });
  };
  const onHandleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, position: ButtonPosition, name: string) => {
    const current = dragRef.current;
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      if (!current) { telegramWebApp()?.disableVerticalSwipes?.(); updateDrag({ source: position, target: position, name, pointerId: null, x: 0, y: 0 }); }
      else finishDrag();
      return;
    }
    if (!current || current.pointerId !== null) return;
    if (event.key === "Escape") { event.preventDefault(); cancelDrag(); return; }
    let target = current.target;
    if (event.key === "ArrowUp" && target.row > 0) target = { row: target.row - 1, button: rows[target.row - 1].buttons.length };
    else if (event.key === "ArrowDown" && target.row < rows.length - 1) target = { row: target.row + 1, button: rows[target.row + 1].buttons.length };
    else if (event.key === "ArrowLeft") target = { ...target, button: Math.max(0, target.button - 1) };
    else if (event.key === "ArrowRight") target = { ...target, button: Math.min(rows[target.row].buttons.length, target.button + 1) };
    else return;
    event.preventDefault();
    updateDrag({ ...current, target });
    setAnnouncement(`${name}: ряд ${target.row + 1}, позиция ${target.button + 1}`);
  };

  const selectedButton = selected ? rows[selected.row]?.buttons[selected.button] : null;
  const invalidSelectedUrl = Boolean(selectedButton?.url.trim() && !guardedHttpUrl(selectedButton.url));
  const inspector = selected && selectedButton && typeof document !== "undefined" ? createPortal(
    <div className="button-inspector-backdrop" onPointerDown={(event) => { if (event.currentTarget === event.target) setSelected(null); }}>
      <section className="button-inspector" role="dialog" aria-modal="true" aria-label={`Настройка кнопки ${selectedButton.text.trim() || `Кнопка ${selected.button + 1}`}`}>
        <header><div><p className="eyebrow">Ряд {selected.row + 1}</p><strong>Настройка кнопки</strong></div><button type="button" aria-label="Закрыть настройку кнопки" onClick={() => setSelected(null)}>×</button></header>
        <label className="field"><span>Текст кнопки</span><input aria-label={`Текст кнопки ${selected.row + 1}.${selected.button + 1}`} maxLength={64} value={selectedButton.text} onChange={(event) => updateButton(selected, { text: event.target.value })} /></label>
        <label className="field"><span>URL</span><input aria-label={`URL кнопки ${selected.row + 1}.${selected.button + 1}`} type="url" value={selectedButton.url} placeholder="https://" aria-invalid={invalidSelectedUrl || undefined} onChange={(event) => updateButton(selected, { url: event.target.value })} />{invalidSelectedUrl && <small className="inline-error">Нужна ссылка http:// или https://</small>}</label>
        <div className="button-emoji-control">
          <span>Иконка Premium</span>
          <div>
            <button type="button" aria-label="Выбрать иконку кнопки" onClick={() => setIconPickerOpen(true)}>
              <b aria-hidden="true">{selectedButton.emojiFallback ?? "😀"}</b>
              {selectedButton.customEmojiId ? "Заменить" : "Выбрать"}
            </button>
            {selectedButton.customEmojiId && <button type="button" aria-label="Удалить иконку кнопки" onClick={() => updateButton(selected, { customEmojiId: undefined, emojiFallback: undefined })}>Убрать</button>}
          </div>
          <small>В каналах без поддержки останется обычный эмодзи.</small>
        </div>
        <fieldset className="button-style-picker"><legend>Цвет в Telegram</legend>{Object.entries(styleLabels).map(([style, label]) => <label className={`button-style-option style-${style}`} key={style}><input type="radio" name={`button-style-${selectedButton.id}`} value={style} checked={selectedButton.style === style} onChange={() => updateButton(selected, { style: style as EditableLayoutButton["style"] })} /><span>{label}</span></label>)}{!richEnabled && <small>Цвет появится только в поддерживаемых версиях Telegram.</small>}</fieldset>
        <div className="button-inspector-actions"><button className="danger-action" type="button" onClick={() => { commit(removeButton(rows, selected), "Кнопка удалена"); setSelected(null); }}>Удалить</button><button className="primary-button" type="button" onClick={() => setSelected(null)}>Готово</button></div>
      </section>
    </div>, document.body
  ) : null;
  const dragOverlay = drag && typeof document !== "undefined" ? createPortal(
    <div className="button-drag-overlay" data-testid="button-drag-overlay" style={drag.pointerId === null ? undefined : { transform: `translate3d(${drag.x + 14}px, ${drag.y + 14}px, 0)` }}><span>⠿</span><strong>{drag.name}</strong></div>,
    document.body
  ) : null;

  return <section className="button-layout-editor" aria-labelledby="button-layout-title">
    <div className="button-layout-heading"><div><strong id="button-layout-title">Кнопки</strong><small>{totalButtons(rows)} из {limits.total}</small></div><div className="button-layout-heading-actions">{undoRows && <button type="button" aria-label="Отменить последнее действие" onClick={() => { const previous = cloneRows(undoRows); setUndoRows(null); onChange(previous); setAnnouncement("Последнее действие отменено"); }}>Отменить</button>}<button type="button" aria-label="Добавить ряд" disabled={totalButtons(rows) >= limits.total} onClick={() => { const rowIndex = rows.length; const next = addRow(rows, { id: newId("row"), buttons: [{ id: newId("button"), text: "", url: "", style: "default" }] }, limits); commit(next, "Новый ряд добавлен"); setSelected({ row: rowIndex, button: 0 }); }}>+ Ряд</button></div></div>
    {rows.map((row, rowIndex) => <div className="button-layout-row" role="group" aria-label={`Ряд кнопок ${rowIndex + 1}`} data-row-index={rowIndex} data-drag-over={drag?.target.row === rowIndex ? "true" : undefined} key={row.id}>
      <div className="button-row-toolbar"><strong>Ряд {rowIndex + 1} · {row.buttons.length}/{limits.perRow}</strong><span><button type="button" aria-label={`Поднять ряд ${rowIndex + 1}`} disabled={rowIndex === 0} onClick={() => commit(moveRow(rows, rowIndex, rowIndex - 1), `Ряд ${rowIndex + 1} поднят`)}>↑</button><button type="button" aria-label={`Опустить ряд ${rowIndex + 1}`} disabled={rowIndex === rows.length - 1} onClick={() => commit(moveRow(rows, rowIndex, rowIndex + 1), `Ряд ${rowIndex + 1} опущен`)}>↓</button><button type="button" aria-label={`Удалить ряд ${rowIndex + 1}`} onClick={() => commit(removeRow(rows, rowIndex), `Ряд ${rowIndex + 1} удалён`)}>×</button></span></div>
      <div className="button-editor-list">{row.buttons.map((button, buttonIndex) => {
        const position = { row: rowIndex, button: buttonIndex };
        const name = button.text.trim() || `Кнопка ${buttonIndex + 1}`;
        const invalidUrl = Boolean(button.url.trim() && !guardedHttpUrl(button.url));
        return <article className={`button-editor-item${invalidUrl ? " invalid" : ""}`} data-button-position={`${rowIndex}:${buttonIndex}`} key={button.id}>
          <button className="button-drag-handle" type="button" aria-label={`Перетащить ${name}`} aria-pressed={drag?.source.row === rowIndex && drag.source.button === buttonIndex} onPointerDown={(event) => { event.preventDefault(); event.currentTarget.setPointerCapture?.(event.pointerId); telegramWebApp()?.disableVerticalSwipes?.(); updateDrag({ source: position, target: position, name, pointerId: event.pointerId, x: event.clientX, y: event.clientY }); }} onPointerMove={onDragMove} onPointerUp={(event) => { if (dragRef.current?.pointerId !== event.pointerId) return; event.currentTarget.releasePointerCapture?.(event.pointerId); finishDrag(); }} onPointerCancel={cancelDrag} onKeyDown={(event) => onHandleKeyDown(event, position, name)}>⠿</button>
          <span className={`button-style-dot style-${button.style}`} aria-label={styleLabels[button.style]} />
          <button className="button-editor-open" type="button" aria-label={`Редактировать ${name}`} onClick={() => setSelected(position)}><span><strong>{name}</strong><small>{invalidUrl ? "Исправьте URL" : button.url || "Добавьте ссылку"}</small></span><b aria-hidden="true">›</b></button>
        </article>;
      })}</div>
      <button className="button-row-add" type="button" aria-label="Добавить кнопку" disabled={row.buttons.length >= limits.perRow || totalButtons(rows) >= limits.total} onClick={() => { const buttonIndex = row.buttons.length; const next = addButton(rows, rowIndex, { id: newId("button"), text: "", url: "", style: "default" }, limits); commit(next, "Кнопка добавлена"); setSelected({ row: rowIndex, button: buttonIndex }); }}>+ Кнопка</button>
    </div>)}
    {!rows.length && <div className="button-layout-empty"><strong>Кнопок пока нет</strong><p>Добавьте ряд — кнопки сразу появятся в предпросмотре.</p></div>}
    {dragOverlay}{inspector}
    {iconPickerOpen && selected && <ButtonEmojiPicker
      onClose={() => setIconPickerOpen(false)}
      onSelect={(emoji) => {
        updateButton(selected, { customEmojiId: emoji.customEmojiId, emojiFallback: emoji.fallback });
        setIconPickerOpen(false);
      }}
    />}
    <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
  </section>;
}
